import { useMap } from "@vis.gl/react-google-maps";
import { Checkbox, Switch } from "antd";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { WorldMapProvider } from "../../../../contexts/WorldMapContext";
import DiseaseZonesBox from '../../../../contexts/WorldMapContext/DiseaseZonesBox';
import GoogleMap from "../../../../contexts/WorldMapContext/GoogleMap";
import { FactoryMarker, SiteMarker, VesselMarker } from "../../../../contexts/WorldMapContext/Marker";
import { SeaWayPolyline } from "../../../../contexts/WorldMapContext/Polyline";
import { selectOpDetail } from "../../../../util/store/selectors";
import { InfoMode, OPERATION_TYPE } from '../../../../util/varibles/constants';
import { selectOpDetail as operationDetailSelectors } from '../../constants';
import stylesContainer from '../../style.module.scss';
import ActualDiseasedZone from './ActualDiseasedzone';

const ContentRoute: React.FC = () => {
    const [isTracking, setIsTracking] = useState(false);
    const [backToSite, setBackToSite] = useState(false);
    const data = useSelector(selectOpDetail.data)
    const actualRoute = useSelector(operationDetailSelectors.actualRoute);
    const infoMode = useSelector(operationDetailSelectors.infoMode);
    const loadingRoute = useSelector(operationDetailSelectors.loadingRoute);
    const initialActualViewRef = useRef<string>();
    const { route, vessel, operation: { id, site, factory, source, destination, avg_speed, operation_type } } = data || {};
    const isHarvest = operation_type === OPERATION_TYPE.HARVEST;
    const hasDiseaseZones = isHarvest || operation_type === OPERATION_TYPE.TREATMENT;
    const actual = hasDiseaseZones && infoMode === InfoMode.ACTUAL;
    const startPlace = site || source;
    const planPoints = route?.points ?? [];
    const firstPlanPoint = planPoints[0];
    const lastPlanPoint = planPoints[planPoints.length - 1];
    const startPosition = actual ? startPlace?.position ?? firstPlanPoint : firstPlanPoint;
    const factoryPosition = actual ? factory?.position ?? lastPlanPoint : lastPlanPoint;
    const destinationPosition = actual ? destination?.position ?? lastPlanPoint : lastPlanPoint;
    const map = useMap('map');
    const routePoints = actual ? actualRoute.real_route.points : route?.points;
    const points = useMemo(() => (routePoints ?? []).map(item => ({
        lat: +item.latitude,
        lng: +item.longitude,
    })), [routePoints]);

    useEffect(() => {
        if (!actual) {
            initialActualViewRef.current = undefined;
            return;
        }
        if (!map || loadingRoute || !id || initialActualViewRef.current === id) return;

        const bounds = new google.maps.LatLngBounds();
        const positions = [
            ...actualRoute.real_route.points,
            site?.position,
            factory?.position,
            source?.position,
            destination?.position,
            startPosition,
            factory ? factoryPosition : undefined,
            destination ? destinationPosition : undefined,
            vessel?.gps ? {
                latitude: vessel.gps.Latitude ?? vessel.position?.latitude,
                longitude: vessel.gps.Longitude ?? vessel.position?.longitude,
            } : vessel?.position,
        ];

        positions.forEach(position => {
            if (position?.latitude == null || position?.longitude == null) return;
            const lat = Number(position.latitude);
            const lng = Number(position.longitude);
            if (Number.isFinite(lat) && Number.isFinite(lng)) bounds.extend({ lat, lng });
        });

        if (bounds.isEmpty()) return;

        map.fitBounds(bounds, 48);
        initialActualViewRef.current = id;
    }, [actual, map, loadingRoute, id, actualRoute.real_route.points, site, factory, source, destination, vessel, startPosition, factoryPosition, destinationPosition]);

    const handleClick = (checked: boolean) => {
        if (checked) {
            const { gps, position } = vessel;
            const { Latitude = position?.latitude || 0, Longitude = position?.longitude || 0 } = gps || {};
            map?.setZoom(7)
            map?.setCenter({ lat: Latitude, lng: Longitude });
        }
        setIsTracking(checked);
    }

    if (!actual && !route)
        return null

    return <div className={actual || isHarvest
        ? `${stylesContainer.section} relative left-1/2 !w-screen -translate-x-1/2 !rounded-none !border-x-0 !border-b-0 !p-0`
        : stylesContainer.section}>
        {actual ? <div className="mx-auto flex min-h-[44px] w-full max-w-[1200px] flex-wrap items-center justify-between gap-3 px-5 py-2">
            <div className="flex items-baseline gap-3">
                <span className="text-[20px]">Actual route</span>
                <span className="text-[15px]">Route Avg. speed: {avg_speed == null ? '-' : `${Number(avg_speed.toFixed(2))} knot/h`}</span>
            </div>
            <div className="flex items-center gap-4 text-[13px]">
                <div className="flex items-center gap-1">
                    <span className="h-3 w-3 rounded-full bg-[#2995a3]" />
                    <span className="h-3 w-3 rounded-full bg-[#c6532b]" />
                    <span className="h-3 w-3 rounded-full bg-[#dda557]" />
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#6542c8] text-[7px] text-white">ISA</span>
                    <span>Site: PD surveillance, ISA protection, ISA surveillance, ISA Confirmed</span>
                </div>
                <div className="flex items-center gap-2">
                    <span>{isHarvest ? 'Track vessel' : 'Back to site'}</span>
                    <Switch size="small" checked={isHarvest ? isTracking : backToSite} onChange={isHarvest ? handleClick : setBackToSite} />
                </div>
            </div>
        </div> : hasDiseaseZones ? <div className="mx-auto flex min-h-[44px] w-full max-w-[1200px] flex-wrap items-center justify-between gap-3 px-5 py-2">
            <div className="flex items-baseline gap-3">
                <span className="text-[20px]">Plan route</span>
                <span className="text-[15px]">{`${route?.name} | ${route?.distance} nm`}</span>
            </div>
            <div className="flex items-center gap-2 text-[13px]">
                <span>Track vessel</span>
                <Switch size="small" checked={isTracking} onChange={handleClick} />
            </div>
        </div> : <div className={stylesContainer['header-box']}>
            <div className="flex justify-content-between align-items-center">
                <div className="flex align-items-center">
                    <span className="mr-3">Route</span>
                    <div className="font-s16 flex align-items-end">
                        {`${route?.name}  |  ${route?.distance} nm`}
                    </div>
                </div>
                <div className="flex align-items-center">
                    <Checkbox {...{
                        className: 'font-s2',
                        checked: isTracking,
                        onChange: ({ target }) => handleClick(target.checked),
                    }}>
                        Track vessel
                    </Checkbox>
                </div>
            </div>
        </div>}
        <div className='w-full h-[660px]'>
            <GoogleMap
                center={actual && backToSite && site?.position
                    ? { lat: +site.position.latitude, lng: +site.position.longitude }
                    : actual ? undefined : points}
                onDragstart={() => { setIsTracking(false); setBackToSite(false); }}
            >
                <VesselMarker
                    data={vessel}
                    showPopup={isTracking}
                />
                {points.length > 0 && <SeaWayPolyline data={{ points }} width={3} {...(actual ? { color: '#56A513' } : {})} />}
                {hasDiseaseZones && <ActualDiseasedZone isShow={actual} diseases={actualRoute.route_diseases} />}
                {hasDiseaseZones && <DiseaseZonesBox />}
                <SiteMarker data={{ ...startPlace, position: startPosition }} />
                {(!actual || isHarvest) && factory && <FactoryMarker data={{ ...factory, position: factoryPosition }} />}
                {(!actual || isHarvest) && destination && <SiteMarker data={{ ...destination, position: destinationPosition }} />}
            </GoogleMap>
        </div>
    </div>
}

const Route: React.FC = () => {

    return <WorldMapProvider>
        <ContentRoute />
    </WorldMapProvider>;
};

export default Route
